import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { IDigitalAddress, IDigitalAddressInput } from '../../../../types/contact';
import { Form, FormFeedback, FormGroup, Input} from 'reactstrap';
import { useApolloClient } from '@apollo/client';
import { ORG_VALUE_LISTS } from '../../../../graphql/valuelist-gql';
import { IValueListLight } from '../../../../types/valuelist';
import { validEmail, validURL } from '../../../../utils/commons';
import Select from '../../../common/select/select';
import Textarea, { TextareaHandler } from '../../../common/textarea/textarea';
import useAuth from '../../../../hook/useAuth';
import { useFrench } from '../../../french/french-context';

export type DigitalAddressHandler = {
    getDigitalAddress: () => IDigitalAddressInput;
    validate: (inclMandatoryCheck: boolean) => boolean;
}

type DigitalAddressDetailsProps = {
    digitalAddress?: IDigitalAddress;
    onChange: () => void;
    autoFocus?: boolean;
}

const DigitalAddressDetails = forwardRef<DigitalAddressHandler, DigitalAddressDetailsProps>((props: DigitalAddressDetailsProps, ref:ForwardedRef<any>) => {
    const  {digitalAddress, onChange, autoFocus} = props

    const [digitalAddressInput, setDigitalAddressInput] = useState<IDigitalAddressInput>()
    const [digitalTypeError, setDigitalTypeError] = useState('')
    const [addressError, setAddressError] = useState('')

    const descRef = useRef<TextareaHandler>(null)

    const client = useApolloClient()
    const {orgValueLists} =  client.readQuery({query: ORG_VALUE_LISTS})
    const [inlineVaidate, setInlineValidate] = useState<boolean>(false)

    const { isFrenchTranslator } = useAuth()
    const [frenchState] = useFrench()

    useEffect(() => {
        setDigitalAddressInput({
            digitalAddressId: digitalAddress?.id || 0,
            digitalTypeId: digitalAddress?.digitalType?.id||orgValueLists?.digitalTypes[0]?.id || 0,
            address: digitalAddress?.digitalType?.code === "EMAIL" ? digitalAddress?.emailAddressTxt : digitalAddress?.netAddressTxt || '',
            description: digitalAddress?.description || '',
            descriptionFrench: digitalAddress?.descriptionFrench || '',
            descriptionFrenchSync: digitalAddress?.descriptionFrenchSync || 0
        })
    }, [digitalAddress])

    useEffect(() => {
        onChange()
    }, [digitalAddressInput])

    useImperativeHandle(ref, () => ({
        getDigitalAddress(): IDigitalAddressInput {
            return digitalAddressInput!
        },
        validate(inclMandatoryCheck: boolean): boolean {
            let valid: boolean = true
            if (descRef.current && !descRef.current.validate()) {
                valid = false
            }
            if (digitalAddressInput?.address && !validateAddress( digitalAddressInput.address, digitalAddressInput?.digitalTypeId)) {
                valid = false
            }
            if (valid && inclMandatoryCheck) {
                setInlineValidate(true)

                if (!digitalAddressInput?.digitalTypeId) {
                    setDigitalTypeError('Digital type is required.')
                    valid = false
                }
                if (!digitalAddressInput?.address) {
                    setAddressError('Address is required.')
                    valid = false
                }

                if (isEmailType(digitalAddressInput?.digitalTypeId)) {
                    if (digitalAddressInput?.address && !validEmail(digitalAddressInput.address)) {
                        setAddressError("The email address is invalid.")
                        valid = false
                    }
                }

                if (isInternetUrl(digitalAddressInput?.digitalTypeId) || isIntranetUrl(digitalAddressInput?.digitalTypeId)) {
                    if (digitalAddressInput?.address && !validURL(digitalAddressInput.address)) {
                        setAddressError(`The ${isInternetUrl(digitalAddressInput?.digitalTypeId) ? 'Internet' : 'Intranet'} address is invalid.`)
                        valid = false
                    }
                }
            }
            return valid
        }
    }), [digitalAddressInput])

    const isEmailType = (digitalTypeId?: number) => {
        return orgValueLists.digitalTypes.find((e: IValueListLight) => e.id === digitalTypeId)?.code === 'EMAIL'
    }

    const isIntranetUrl = (digitalTypeId?: number) => {
        return orgValueLists.digitalTypes.find((e: IValueListLight) => e.id === digitalTypeId)?.code === 'ENINTRA'
    }

    const isInternetUrl = (digitalTypeId?: number) => {
        return orgValueLists.digitalTypes.find((e: IValueListLight) => e.id === digitalTypeId)?.code === 'ENINTER'
    }

    const changeDigitalType = (event: any) => {
        if (event.target.value) {
            setDigitalTypeError('')
        }
        if (digitalAddressInput?.address) {
            validateAddress(digitalAddressInput.address, event.target.value)
        }
        setDigitalAddressInput({...digitalAddressInput,  digitalTypeId: event.target.value})
    }

    const changeAddress = (event: any) => {
        validateAddress(event.target.value, digitalAddressInput?.digitalTypeId)
        setDigitalAddressInput({...digitalAddressInput, address: event.target.value.trim()})
    }
    const changeDescription = (value: string) => {
        if (value && value.trim().length > 0) {
            setDigitalAddressInput({...digitalAddressInput, description: value, descriptionFrenchSync: 0});
        }
        else {
            setDigitalAddressInput({...digitalAddressInput, description: '', descriptionFrench: '', descriptionFrenchSync: 0})
        }
    }

    const validateAddress = (address: string, digitalTypeId?: number): boolean => {
        if (isEmailType(digitalTypeId)) {
            if (!address || validEmail(address) && address.length <= 100) {
                setAddressError('')
            } else if (inlineVaidate) {
                setAddressError("The email address is invalid.")
                return false
            }
            if (address.length > 100) {
                setAddressError("This value is too long. It should have 100 characters or fewer.")
                return false
            }
        } else if (isInternetUrl(digitalTypeId) || isIntranetUrl(digitalTypeId)) {
            if (!address || validURL(address) && address.length <= 255) {
                setAddressError('')
            } else if (inlineVaidate) {
                setAddressError(`The ${isInternetUrl(digitalTypeId) ? 'Internet' : 'Intranet'} address is invalid.`)
                return false
            }
            if (address.length > 255) {
                setAddressError("This value is too long. It should have 255 characters or fewer.")
                return false
            }
        }
        return true
    }

    const changeDescriptionTranslation = (event: any) => {
        setDigitalAddressInput({...digitalAddressInput, descriptionFrenchSync: event.target.checked? 1:0})
    }

    return (
        <Form className='oms-container'>
            <div className='row'>
                <div className='col-md-3'><label htmlFor="digitalType">Digital Type: {!digitalAddress ? '(required)': ''}</label></div>
                { !isFrenchTranslator && <div className='col-md-9'>
                    <Select id="digitalType"
                        value={digitalAddressInput?.digitalTypeId}
                        onChange={changeDigitalType}
                        style={{width:'100%' }}
                        required
                        invalid={!!digitalTypeError}
                        disabled={!!digitalAddress}
                        autoFocus={autoFocus && !digitalAddress}>
                        {orgValueLists.digitalTypes?.map((t: IValueListLight) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </Select>
                    <FormFeedback>{digitalTypeError}</FormFeedback>
                </div>
              }
                {isFrenchTranslator && <div className='col-md-9'><span id="digitalType">
                    {orgValueLists.digitalTypes.map((item: IValueListLight) => {
                        if (item.id === digitalAddressInput?.digitalTypeId) {
                            return item.label;
                        }
                        return '';
                    })}
                </span></div>}
            </div>
            <div className='row'>
                <div className='col-md-3'><label htmlFor="digitalAddress">Address: (required)</label></div>
                { !isFrenchTranslator && <div className='col-md-9'>
                    <Input id="digitalAddress"
                        type="text"
                        style={{width: '100%'}}
                        value={digitalAddressInput?.address}
                        onChange={changeAddress}
                        placeholder='Enter an address'
                        invalid={!!addressError}
                        required
                        autoFocus={autoFocus && !!digitalAddress}
                    />
                    <FormFeedback>{addressError}</FormFeedback>
                </div>
                }
                {isFrenchTranslator && <div className='col-md-9'><span id="digitalAddress">{digitalAddressInput?.address} </span></div>}
            </div>
            <div className='row'>
                <div className='col-md-3'><label htmlFor="digitalAddressDesc">Description:</label></div>
                { !isFrenchTranslator &&  <div className='col-md-9'>
                    <Textarea id="digitalAddressDesc"
                        ref={descRef}
                        rows={3}
                        maxLength={255}
                        value={digitalAddressInput?.description}
                        onValueChange={changeDescription}
                        placeholder='Enter a description for the digital address'/>
                </div>
                }
                {isFrenchTranslator && <div className='col-md-9'><span id="digitalAddressDesc" >{digitalAddressInput?.description} </span></div>}
            </div>
            {frenchState.isFrenchChecked &&
                <>
                    <div className='row'>
                        <div className='col-md-4'><label htmlFor="phoneDescFrench">Description (French): </label></div>
                        <div className='col-md-8'><span id='phoneDescFrench' lang="fr">{digitalAddressInput?.descriptionFrench}</span></div>
                    </div>
                    <div className='row'>
                        <div className='col-md-12'>
                            <Input id="descriptionFrenchSync" type="checkbox" checked={digitalAddressInput?.descriptionFrenchSync === 1 ? true : false} onChange={changeDescriptionTranslation}  disabled = {!digitalAddressInput?.descriptionFrench}/> {' '}
                            <label htmlFor="descriptionFrenchSync">Translated Description</label>
                        </div>
                    </div>
                </>
            }
        </Form>
    )
})

export default DigitalAddressDetails
